import {Feature, FeatureWithDistance, LngLat} from "./DistributorsMap.types";

/**
 * Returns the (approximate) distance between two points, defined by their lon/lat coordinates.
 *
 * The distance is good enough for our usecase, but it ignores that the earth is not a perfect sphere.
 *
 * The coordinates must be in the WGS84 reference system, which is used by Google Maps
 * (cf. https://developers.google.com/maps/documentation/javascript/coordinates)
 *
 * @see https://stackoverflow.com/a/27943 for the source of the algorithm
 */
export function distanceKm(coord1: LngLat, coord2: LngLat): number {
  const [lon1, lat1] = coord1;
  const [lon2, lat2] = coord2;

  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2-lat1);  // deg2rad below
  const dLon = deg2rad(lon2-lon1);
  const a =
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
    Math.sin(dLon/2) * Math.sin(dLon/2)
  ;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return R * c; // Distance in km
}

export function deg2rad(deg: number): number {
  return deg * (Math.PI / 180)
}

export function rad2deg(rad: number): number {
  return rad * (180 / Math.PI);
}

export function debounce(func: () => void, timeout = 300) {
  let timer: number;
  return (...args: any[]) => {
    clearTimeout(timer);
    timer = window.setTimeout(() => { func.apply(this, args); }, timeout);
  };
}

export const frequencySort = <T>(arr: T[] = []): T[] => {
  const map: { [key: string]: number } = {};
  for (let i = 0; i < arr.length; i++) {
    const key = arr[i].toString();
    map[key] = (map[key] || 0) + 1;
  }
  return arr.sort(
    (a, b) =>
      map[b.toString()] - map[a.toString()] ||
      a.toString().localeCompare(b.toString())
  );
};

export const isStringContainSpace = (str: string): boolean => {
  const regex = /\s+/g;

  return str.match(regex) !== null;
};


export const replaceLast = (
  str: string,
  pattern: RegExp | string,
  replacement: string
): string => {
  const match: string =
    typeof pattern === "string"
      ? pattern
      : (str.match(new RegExp(pattern.source, "g")) || []).slice(-1)[0];

  if (!match) return str;

  const last: number = str.lastIndexOf(match);

  return last !== -1
    ? `${str.slice(0, last)}${replacement}${str.slice(last + match.length)}`
    : str;
};

// An entry from the dealer list as returned by the Pimcore API, see https://pim.bikeleasing.de/api/distributor/maps-data
type DealerListEntry = { lat: number, lng: number, title: string, icon: string, info: string };

export const convertDistributorToFeature = ({lat, lng, title, icon, info}: DealerListEntry, id: number): Feature => ({
  id: String(id),
  type: "Feature",
  geometry: {
    type: "Point",
    coordinates: [lng, lat],
  },
  properties: {
    name: title,
    info: info,
    icon: icon,
    tag: "Assets",
  },
});
